import { DestroyRef, Injectable } from '@angular/core';
import { map, Observable, Subject, switchMap, tap, finalize, of } from 'rxjs';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { emitOnce } from '@ngneat/elf';
import { ReviewContentChatsHttpService } from '../../base/review-content-chats-http.service';
import {
  ImageChatMessageContentStoreModel,
  ImageChatMessageStoreModel,
  ImageIndexToChatIdProps,
  createUserMessageImageChatStoreModel,
  mapImageChatMessageTableRowToStoreModel,
} from '../models';
import { ImageChatMessageQuery, ImageChatMessageStore } from '../store';
import { ImageChatMessageHttpService } from './image-chat-message-http.service';

@Injectable()
export class ImageChatMessageService {
  constructor(
    private readonly store: ImageChatMessageStore,
    private readonly query: ImageChatMessageQuery,
    private readonly chatMessageHttpService: ImageChatMessageHttpService,
    private readonly reviewContentChatsHttpService: ReviewContentChatsHttpService,
    private readonly destroyRef: DestroyRef,
  ) {}

  private readonly fetchTrigger$ = new Subject<void>();

  triggerChatMessagesFetch() {
    this.fetchTrigger$.next();
  }

  resetChat() {
    this.store.reset();
  }

  createReviewImageChat(imageJsonPath: string, { force }: { force?: boolean } = {}): Observable<number | undefined> {
    const existingChatId = this.query.getProp('imageIndexToChatId')[imageJsonPath]?.chatId;
    if (existingChatId && !force) {
      return of(existingChatId);
    }

    emitOnce(() => {
      if (existingChatId) {
        this.query
          .getEntitiesByPredicate((entity) => entity.llm_chat_id === existingChatId)
          .forEach((entity) => {
            this.store.delete(entity.id);
          });
      }

      this.store.updateProps((props) => {
        const { [imageJsonPath]: _, ...rest } = props.imageIndexToChatId;
        return {
          ...props,
          imageIndexToChatId: rest,
        };
      });
    });

    const reviewableItemId = this.query.getProp('reviewableItemId');
    assertNonNullable(reviewableItemId, 'Reviewable item ID not found');

    return this.reviewContentChatsHttpService.createContentChat(reviewableItemId, imageJsonPath).pipe(
      tap((reviewImageChat) => {
        this.store.updateProps((props) => ({
          ...props,
          imageIndexToChatId: {
            ...props.imageIndexToChatId,
            [imageJsonPath]: {
              chatId: reviewImageChat.llm_chat_id,
              requestId: null,
            },
          },
        }));
      }),
      map((reviewImageChat) => reviewImageChat.llm_chat_id),
    );
  }

  fetchReviewImageChats(reviewableItemId: string, createdBy: string) {
    this.resetChat();

    this.store.setLoading(true);

    this.store.updateProps((props) => ({
      ...props,
      reviewableItemId,
    }));

    return this.reviewContentChatsHttpService.fetchByReviewableItem(reviewableItemId, createdBy).pipe(
      map((imageChats) => {
        if (imageChats.length === 0) {
          return [];
        }

        const filteredImageChatsMap = imageChats.reduce(
          (acc, imageChat) => {
            if (acc[imageChat.content_json_path]) {
              // imageChats is sorted by created_at, thus newest chats should be at the beginning of the array, thus if the image_index already exists, do not override it
              return acc;
            }

            acc[imageChat.content_json_path] = {
              chatId: imageChat.llm_chat_id,
              requestId: null,
            };
            return acc;
          },
          {} as Record<string, ImageIndexToChatIdProps>,
        );

        this.store.updateProps((props) => ({
          ...props,
          imageIndexToChatId: filteredImageChatsMap,
        }));

        return Object.values(filteredImageChatsMap);
      }),
      switchMap((filteredImageChats) => {
        if (filteredImageChats.length === 0) {
          return of(null);
        }

        return this.fetchMessageByChatIds(filteredImageChats.map((imageChat) => imageChat.chatId));
      }),
      finalize(() => {
        this.store.setLoading(false);
      }),
    );
  }

  fetchMessageByIds(messageIds: number[]): Observable<ImageChatMessageStoreModel[]> {
    return this.chatMessageHttpService.fetchMessageByIds(messageIds).pipe(
      map((models) => models.map(mapImageChatMessageTableRowToStoreModel)),
      tap((models) => this.store.upsertMany(models)),
    );
  }

  fetchMessageByRequestId(requestId: string, chatId: number): Observable<ImageChatMessageStoreModel[]> {
    return this.chatMessageHttpService.fetchMessagesByRequestId(requestId, chatId).pipe(
      map((models) => models.map(mapImageChatMessageTableRowToStoreModel)),
      tap((models) => this.store.upsertMany(models)),
    );
  }

  fetchMessageByChatIds(chatIds: number[]): Observable<ImageChatMessageStoreModel[]> {
    return this.chatMessageHttpService.fetchMessageByChatIds(chatIds).pipe(
      map((models) => models.map(mapImageChatMessageTableRowToStoreModel)),
      tap((models) => this.store.upsertMany(models)),
    );
  }

  sendMessage(message: ImageChatMessageContentStoreModel, imageJsonPath: string) {
    const chatId = this.query.getProp('imageIndexToChatId')[imageJsonPath]?.chatId;

    assertNonNullable(chatId, 'Chat ID not found');

    const locallyCreatedMessage = createUserMessageImageChatStoreModel(message, chatId);

    this.store.create([locallyCreatedMessage]);
    this.updateImagePropsWithStartedRequestId('progressive-ui-temporary-request-id', imageJsonPath);

    return (
      this.chatMessageHttpService
        .sendMessage(message.text, chatId)
        .pipe(
          tap(({ request_id }) => {
            this.updateImagePropsWithStartedRequestId(request_id ?? null, imageJsonPath);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        // not in component, because first message in chat takes a lot of time to complete, thus subscription inside the component will be cancelling request when tab/component is closed
        .subscribe()
    );
  }

  updateImagePropsWithStartedRequestId(requestId: string | null, imageJsonPath: string) {
    this.store.updateProps((props) => ({
      ...props,
      imageIndexToChatId: {
        ...props.imageIndexToChatId,
        [imageJsonPath]: { chatId: props.imageIndexToChatId[imageJsonPath].chatId, requestId },
      },
    }));
  }
}
